<script setup lang="ts">
import { Button } from "@/components/ui/button";
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from "@/components/ui/table";
import { format } from "date-fns";
import type { Reservation } from "../../Dashboard/components/event.vue";
import type { PaginatedData } from "@/types/Pagination";

import {
    Pagination,
    PaginationEllipsis,
    PaginationList,
    PaginationListItem,
    PaginationNext,
    PaginationPrev,
} from "@/components/ui/pagination";
import { useLocationOptions } from "../../EventTypes/useLocationOptions";
import { User } from "../../EventTypes/types/EventType";
const props = defineProps<{
    data: PaginatedData<Reservation>;
    teamMembers: User[];
}>();
const { getLocationForId } = useLocationOptions();
const emit = defineEmits(["prevPage", "nextPage", "setPage"]);

function getTeamMemeberForEvent(userId: number) {
    return props.teamMembers.find((u) => u.id === userId);
}
</script>

<template>
    <div class="w-full">
        <div class="flex items-center gap-2 py-4"></div>
        <div>
            <Table>
                <TableHeader class="border-t border-iron-200">
                    <TableRow>
                        <TableHead>
                            {{ $t("team_reports.table.team_member") }}
                        </TableHead>
                        <TableHead>
                            {{ $t("team_reports.table.name") }}</TableHead
                        >
                        <TableHead>
                            <div class="flex gap-4">
                                {{ $t("team_reports.table.reservation_start") }}

                                <svg
                                    width="20"
                                    height="20"
                                    viewBox="0 0 20 20"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                >
                                    <path
                                        d="M4.16667 9.99999L10 15.8333M10 15.8333L15.8333 9.99999M10 15.8333V4.16666"
                                        stroke="#3F3F46"
                                        stroke-width="1.75"
                                        stroke-linecap="round"
                                        stroke-linejoin="round"
                                    />
                                </svg>
                            </div>
                        </TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    <TableRow
                        :class="[i % 2 === 0 ? 'bg-white' : 'bg-iron-50']"
                        v-for="(row, i) of data.data"
                        :key="row.id"
                    >
                        <TableCell class="font-medium">
                            <div class="flex items-center gap-3">
                                <div>
                                    <img
                                        class="h-10 w-10 rounded-md"
                                        :src="
                                            getTeamMemeberForEvent(row.user_id)
                                                ?.profile_photo_url
                                        "
                                        alt=""
                                    />
                                </div>
                                <div>
                                    <p class="text-sm text-iron-700">
                                        {{
                                            getTeamMemeberForEvent(row.user_id)
                                                ?.name
                                        }}
                                    </p>
                                    <p class="text-xs text-iron-500">
                                        {{
                                            getTeamMemeberForEvent(row.user_id)
                                                ?.email
                                        }}
                                    </p>
                                </div>
                            </div>
                        </TableCell>
                        <TableCell>
                            <div>
                                <p class="text-sm font-medium text-iron-700">
                                    {{ row.firstname + " " + row.lastname }}
                                </p>
                                <p class="text-xs text-iron-500">
                                    {{ row.email }}
                                </p>
                            </div>
                        </TableCell>
                        <TableCell>
                            <div>
                                <p class="text-iron-700">
                                    {{ format(new Date(row.start), "PPpp") }}
                                </p>
                                <p class="text-xs text-iron-500">
                                    {{
                                        getLocationForId(row.location.type)
                                            ?.name
                                    }}
                                </p>
                            </div>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </div>

        <div
            class="flex w-full items-center justify-center space-x-2 px-4 py-4"
        >
            <Pagination
                v-slot="{ page }"
                :total="data.total"
                :sibling-count="1"
                show-edges
                :default-page="1"
                class="w-full"
            >
                <PaginationList
                    v-slot="{ items }"
                    class="flex w-full items-center justify-center gap-1"
                >
                    <PaginationPrev @click="emit('prevPage')" />

                    <div class="flex w-full flex-1 justify-center gap-2">
                        <template v-for="(item, index) in items">
                            <PaginationListItem
                                v-if="item.type === 'page'"
                                :key="index"
                                :value="item.value"
                                as-child
                            >
                                <Button
                                    @click="emit('setPage', item.value)"
                                    class="h-10 w-10 p-0"
                                    :variant="
                                        item.value === page
                                            ? 'secondary'
                                            : 'ghost'
                                    "
                                >
                                    {{ item.value }}
                                </Button>
                            </PaginationListItem>
                            <PaginationEllipsis
                                v-else
                                :key="item.type"
                                :index="index"
                            />
                        </template>
                    </div>
                    <PaginationNext @click="emit('nextPage')" />
                </PaginationList>
            </Pagination>
        </div>
    </div>
</template>
